import { createFileRoute } from "@tanstack/react-router";
import { Navbar } from "@web/components/Navbar";
import { useAppContext } from "@web/route-services/root/AppContext";

interface ChangeLogEntry {
  /** ISO date string */
  date: string;
  /** Bullet-point changes */
  items: string[];
  /** Bullet-point list for features currently being worked on for the next release (optional) */
  workingOnNext?: string[];
}

// NOTE: Keep this array in chronological order, newest first.
const CHANGELOG: ChangeLogEntry[] = [
  {
    date: "2025-06-15",
    items: [
      "Mostly a maintainance patch.",
      "Updated to react 19.",
      "Fixed a bunch of perf issues on the front page.",
      "Perf should be better now on mobile.",
      "Up max context of proxy provider to 128k.",
      "Fixed issue where changing the context length from the default would cause the proxy option to be unselected (sorry).",
      "There's now a max of 64 instead of 16 alt greetings, why not.",
      "Featured section is randomly ordered now"
    ]
  }
];

export const Route = createFileRoute("/changelog")({
  component: ChangelogPage
});

function ChangelogPage() {
  const { isDesktopScreen } = useAppContext();

  const content = (
    <div className="text-tx-primary flex w-full flex-col space-y-10 p-8">
      <h1 className="font-display text-4xl font-bold">Changelog</h1>

      <div className="flex flex-col space-y-8">
        {CHANGELOG.map((entry) => (
          <section key={entry.date} className="flex flex-col space-y-3">
            <h2 className="text-tx-primary text-2xl font-semibold">
              <time dateTime={entry.date}>{entry.date}</time>
            </h2>
            <ul className="list-disc space-y-2 pl-6 text-lg">
              {entry.items.map((item, idx) => (
                <li key={idx}>{item}</li>
              ))}
            </ul>

            {/* Optional: list of items currently being worked on for the next release */}
            {entry.workingOnNext?.length ? (
              <div className="flex flex-col space-y-2 pt-2">
                <h3 className="text-xl font-semibold">Working on next</h3>
                <ul className="list-disc space-y-2 pl-6 text-lg">
                  {entry.workingOnNext.map((item, idx) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>
        ))}
      </div>
    </div>
  );

  if (isDesktopScreen) {
    return (
      <div className="from-background-gradient via-background to-background flex h-screen w-screen bg-gradient-to-b antialiased">
        <Navbar />
        {/* Scrollable content area */}
        <div style={{ scrollbarGutter: "stable" }} className="scroll-changelog ml-2.5 flex h-full w-full overflow-auto">
          {content}
        </div>
      </div>
    );
  }

  return (
    <>
      <Navbar />
      <main className="from-background-gradient via-background to-background min-h-screen w-full bg-gradient-to-b pb-24 pt-16 antialiased">
        {content}
      </main>
    </>
  );
}
